import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiService from '../services/api';

const OrgContext = createContext(null);

/**
 * OrgProvider — single source of truth for hierarchy + team data.
 * Both HierarchyView and TeamManagement read from / write to this context.
 * Any mutation calls refresh() so both pages stay in sync instantly.
 */
export const OrgProvider = ({ children }) => {
  const [hierarchyTree, setHierarchyTree] = useState([]);   // nested tree from API
  const [flatUsers, setFlatUsers]         = useState([]);   // flat list of all users in tree
  const [unassignedUsers, setUnassignedUsers] = useState([]); // users not placed in hierarchy
  const [teams, setTeams]                 = useState([]);   // team groups
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [refreshKey, setRefreshKey]       = useState(0);

  // Flatten tree helper
  const flattenTree = useCallback((nodes, acc = []) => {
    for (const n of nodes) {
      acc.push(n);
      if (n.children?.length) flattenTree(n.children, acc);
    }
    return acc;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Load hierarchy and teams independently — failure in one shouldn't block the other
    try {
      const treeRes = await apiService.getHierarchyTree();
      if (treeRes.success) {
        const tree = treeRes.data || [];
        setHierarchyTree(tree);
        const allUsers = flattenTree(tree);
        const unassigned = treeRes.unassigned || [];
        // Merge unassigned into flatUsers if not already in tree
        const treeIds = new Set(allUsers.map(u => u.id));
        const extraUnassigned = unassigned.filter(u => !treeIds.has(u.id));
        const combined = [...allUsers, ...extraUnassigned];
        setFlatUsers(combined);
        setUnassignedUsers(unassigned);

        // ── Fallback: if tree returned 0 users, load from admin users list ──
        // This handles the case where users exist but have no hierarchy placement yet
        if (combined.length === 0) {
          try {
            const usersRes = await apiService.getClientAdminUsers({ limit: 500 });
            const rawUsers = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || []);
            if (usersRes.success && rawUsers.length > 0) {
              const fallbackUsers = rawUsers.map(u => ({
                id: parseInt(u.id),
                firstname: u.firstname,
                lastname: u.lastname,
                email: u.email,
                profile_image: u.profile_image || null,
                status: u.status,
                reports_to: null,
                hierarchy_level: null,
                role_name: u.role_name || null,
                trademark_count: 0,
                pending_tasks: 0,
                is_unassigned: true,
                children: [],
              }));
              setFlatUsers(fallbackUsers);
              setUnassignedUsers(fallbackUsers);
              // console.info('[OrgContext] Hierarchy empty — loaded users from admin list as fallback');
            }
          } catch (fallbackErr) {
            // console.error('[OrgContext] fallback users load failed:', fallbackErr);
          }
        }
      } else {
        // console.error('[OrgContext] getHierarchyTree failed:', treeRes.message);
        setError(treeRes.message || 'Failed to load hierarchy');
      }
    } catch (e) {
      // console.error('[OrgContext] getHierarchyTree error:', e);
      setError('Failed to load hierarchy data.');
    }

    try {
      const teamsRes = await apiService.getTeams();
      if (teamsRes.success) {
        setTeams(teamsRes.data || []);
      } else {
        // console.error('[OrgContext] getTeams failed:', teamsRes.message);
      }
    } catch (e) {
      // console.error('[OrgContext] getTeams error:', e);
    }

    setLoading(false);
  }, [flattenTree]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  // ── Hierarchy mutations ──────────────────────────────────────────────────
  const setReportsTo = useCallback(async (userId, managerId, hierarchyLevel, assignToOwner = false) => {
    const res = await apiService.setUserReportsTo(userId, managerId, hierarchyLevel, assignToOwner);
    if (!res.success) throw new Error(res.message || 'Failed to update');
    refresh();
    return res;
  }, [refresh]);

  // ── Hierarchy — promote DRs (Bug 4: move user only, promote their reports up) ──
  const promoteDirectReports = useCallback(async (userId, newManagerId) => {
    const res = await apiService.promoteDirectReports(userId, newManagerId);
    if (!res.success) throw new Error(res.message || 'Failed to promote reports');
    refresh();
    return res;
  }, [refresh]);

  // ── Team mutations ───────────────────────────────────────────────────────
  const createTeam = useCallback(async (payload) => {
    const res = await apiService.createTeam(payload);
    if (!res.success) throw new Error(res.message || 'Failed to create team');
    refresh();
    return res;
  }, [refresh]);

  const updateTeam = useCallback(async (teamId, payload) => {
    const res = await apiService.updateTeam(teamId, payload);
    if (!res.success) throw new Error(res.message || 'Failed to update team');
    refresh();
    return res;
  }, [refresh]);

  const deleteTeam = useCallback(async (teamId) => {
    const res = await apiService.deleteTeam(teamId);
    if (!res.success) throw new Error(res.message || 'Failed to delete team');
    refresh();
    return res;
  }, [refresh]);

  // ── Derived helpers ──────────────────────────────────────────────────────
  // Get all users who are direct reports of a given manager id
  const getDirectReports = useCallback((managerId) =>
    flatUsers.filter(u => u.reports_to === managerId),
  [flatUsers]);

  // Get the team(s) a user belongs to
  const getUserTeams = useCallback((userId) =>
    teams.filter(t => t.members?.some(m => m.id === userId || m.id === String(userId))),
  [teams]);

  // Effective permissions for a user: their role permissions + team manager override
  const getEffectivePermissions = useCallback((userId) => {
    const user = flatUsers.find(u => u.id === userId);
    if (!user) return {};
    const base = user.role_permissions || {};
    // If this user is a manager of any team, they inherit view_all_tasks
    const isManager = teams.some(t => String(t.manager_id) === String(userId));
    return { ...base, view_all_tasks: base.view_all_tasks || isManager };
  }, [flatUsers, teams]);

  return (
    <OrgContext.Provider value={{
      hierarchyTree, flatUsers, unassignedUsers, teams,
      loading, error, refresh,
      setReportsTo, promoteDirectReports, createTeam, updateTeam, deleteTeam,
      getDirectReports, getUserTeams, getEffectivePermissions,
    }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
};

export default OrgContext;